import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPencilSquare,
    faGear,
    faMicrochip,
    faFileLines,
    faPlus,
    faSave,
    faSpinner,
    faHistory,
    faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import patientService from '../../../../services/patientService';
import practiceService from '../../../../services/practiceService';
import inventoryService from '../../../../services/inventoryService';
import ScheduleTargetSettings from '../../ScheduleTargetSettings/ScheduleTargetSettings';
import PatientDocumentsSection from '../../../../components/PatientDocuments/PatientDocumentsSection';
import ActionButtons from '../../../../components/Common/ActionButtons/ActionButtons';
import StatusBadge from '../../../../components/Common/StatusBadge/StatusBadge';
import { PatientAscStyleSinglePicker, PatientAscStyleMultiPicker, buildCaregiverOptionsWithStubs } from '../shared/PatientCaregiverPickers';
import { buildPracticePatientTabPath, isFromPracticeDashboard } from '../../../../utils/practiceNavigation';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import PatientsLogModal, { PatientsLogContent } from '../../../../components/PatientsLogModal/PatientsLogModal';
import { TIME_ZONES, normalizeTimeZone, EMERGENCY_CONTACT_RELATIONS } from '../../../../utils/constants';
import { buildPatientProfileImageUrl } from '../../../../utils/patientProfileImage.js';
import useUsernameEmailAvailability from '../../../../hooks/useUsernameEmailAvailability';
import { preserveAvailabilityErrors, clearFieldError, validateRequiredPhone, updateFieldErrorOnChange } from '../../../../utils/formValidation';
import {
    sanitizeInsuranceField,
    getInsuranceFieldError,
    validateInsuranceData,
    insuranceErrorKey,
} from '../../../../utils/insuranceFieldValidation';
import { getEmailValidationError } from '../../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../../utils/usernameValidation';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError, normalizePhoneDigits } from '../../../../utils/phoneUtils';
import PhoneNumberInput from '../../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import AppDateInput from '../../../../components/Common/AppDateInput/AppDateInput';
import { sortCareProgramsForDisplay } from '../../../../utils/careProgramUtils';
import {
    METER_EXTENSION_OPTIONS,
    normalizeDeviceSerialInput,
    normalizeProductsDropdownResponse,
    validateMeterMappingList,
    getDeviceSerialNumberError,
    formatMeterExtensionLabel,
    formatMeterProductOptionLabel,
    resolveMeterProductDisplayLabel,
} from '../../../../utils/meterMappingUtils';
import lookupService from '../../../../services/lookupService';
import IcdCodeSearchField from '../../../../components/Common/IcdCodeSearchField/IcdCodeSearchField';
import {
    formatIcdOptionLabel,
    getIcdOptionsForMonitoringLabel,
    groupIcdCodesByMeasureType,
    icdOptionsIncludeCode,
} from '../../../../utils/patientMonitoringIcdUtils';
import { isEvitalsMonitoringPractice, MEASURED_TYPE_IDS, normalizePracticeMonitoringTypes, getPracticeMonitoringSectionFlags } from '../../../../utils/practiceMonitoringUtils';
import { parseRawChronicConditionItems } from '../../../../utils/patientBillingUtils';
import { pcmCcmToggleConflictMessage } from '../../../../utils/pcmCcmEnrollment';
import { normalizePatientStatusId, patientStatusFormValue } from '../../../../utils/patientStatusUtils';
import PatientStatusDeviceRemovalModal from '../../../../components/Common/PatientStatusDeviceRemovalModal/PatientStatusDeviceRemovalModal';
import './PatientEditView.css';
import '../AddPatient/AddPatient.css';
import '../AddPatient/AddPatientModal.css';

/**
 * PatientEditView — `/practices/:practiceId/patients/:patientId/edit` (opened from patient profile).
 * Not the same as AddPatient with `?editId=` (patient list single-page edit).
 */

const LOG_MODULE_PATIENT = 'Patient';
const LOG_MODULE_SCHEDULE = 'Patient Schedule';
const LOG_MODULE_METER = 'Patient Meter';
const LOG_MODULE_DOCUMENT = 'Patient Document';

function normalizeMappedDeviceRows(rows) {
    if (!Array.isArray(rows)) return [];
    return rows
        .filter((m) => m && (m.id != null || m.serial_number || m.serialNumber || m.model_number || m.modelNumber))
        .map((m) => ({
            id: m.id,
            model_number: m.model_number ?? m.modelNumber ?? '',
            extension: m.extension ?? '',
            serial_number: m.serial_number ?? m.serialNumber ?? '',
            gateway_serial_number: m.gateway_serial_number ?? m.gatewaySerialNumber ?? ''
        }));
}

function mappedDevicesToMeterMappingsPayload(rows) {
    if (!Array.isArray(rows)) return [];
    const mapped = rows
        .filter(
            (r) =>
                String(r.model_number || '').trim() &&
                String(r.extension || '').trim() &&
                String(r.serial_number || '').trim()
        )
        .map((r) => ({
            modelNumber: String(r.model_number).trim(),
            extension: String(r.extension).trim(),
            serialNumber: String(r.serial_number).trim(),
            gatewaySerialNumber: String(r.gateway_serial_number || '').trim()
        }));
    const seen = new Set();
    return mapped.filter((r) => {
        const k = `${r.modelNumber}|${r.extension}|${r.serialNumber}`.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
    });
}

const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

function formatDate(dateString) {
    if (!dateString) return '-';
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return '-';
    }
}

const STATE_ID_TO_ABBR = Object.fromEntries(
    US_STATES.map((abbr, i) => [i + 1, abbr])
);
const STATE_ABBR_TO_ID = Object.fromEntries(
    US_STATES.map((abbr, i) => [abbr, i + 1])
);

const formatPhoneDisplay = formatUSPhoneNumber;

function isValidEmailFormat(email) {
    return getEmailValidationError(email, { required: false }) === null && String(email ?? '').trim() !== '';
}

function stateSelectValueFromPatient(p) {
    const sid = p?.state_id ?? p?.address?.state_id;
    if (sid == null || sid === '') return '';
    const n = parseInt(String(sid), 10);
    if (!Number.isNaN(n) && STATE_ID_TO_ABBR[n]) return STATE_ID_TO_ABBR[n];
    const u = String(sid).toUpperCase();
    return US_STATES.includes(u) ? u : '';
}

/** Split API ids that were stored as one comma-separated string (e.g. `['123,456,789']`). */
function expandCaregiverIdsFromApi(raw, singleFallback) {
    const parts = [];
    const pushToken = (t) => {
        const s = String(t).trim();
        if (!s) return;
        if (s.startsWith('[')) {
            try {
                const inner = JSON.parse(s);
                if (Array.isArray(inner)) {
                    inner.forEach((x) => pushToken(x));
                    return;
                }
            } catch {
                /* fall through */
            }
        }
        if (s.includes(',')) s.split(',').forEach((p) => pushToken(p.trim()));
        else parts.push(s.replace(/^\[|\]$/g, ''));
    };
    if (Array.isArray(raw) && raw.length > 0) {
        raw.forEach((x) => pushToken(x));
        return [...new Set(parts.filter(Boolean))];
    }
    const one = singleFallback;
    if (one != null && String(one).trim() !== '') {
        pushToken(one);
        return [...new Set(parts.filter(Boolean))];
    }
    return [];
}

export default function PatientEditView() {
    const { practiceId, patientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

    const [activeTab, setActiveTab] = useState('patient-info');
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const documentsSectionRef = useRef(null);
    const [pendingDocumentCount, setPendingDocumentCount] = useState(0);

    // Data State
    const [patientData, setPatientData] = useState(null);
    const [practiceProviders, setPracticeProviders] = useState([]);
    const [practiceCaregivers, setPracticeCaregivers] = useState([]);
    const [systemCaregivers, setSystemCaregivers] = useState([]);
    const [products, setProducts] = useState([]);
    const [mappedDevices, setMappedDevices] = useState([]);
    const [statusDeviceRemovalModal, setStatusDeviceRemovalModal] = useState({ show: false, targetStatus: '' });

    const handleConfirmStatusDeviceRemoval = () => {
        const target = statusDeviceRemovalModal.targetStatus || 'Pending';
        setFormData(prev => ({ ...prev, status: target }));
        setMappedDevices([]);
        setStatusDeviceRemovalModal({ show: false, targetStatus: '' });
        toast.info(`Assigned devices removed from list. Click Update to save patient status as ${target}.`);
    };

    const handleCancelStatusDeviceRemoval = () => {
        setStatusDeviceRemovalModal({ show: false, targetStatus: '' });
        setFormData(prev => ({ ...prev, status: 'Active' }));
    };

    // Form State - Tab 1: Edit Patient
    const [formData, setFormData] = useState({
        title: '', firstName: '', middleInitial: '', lastName: '', username: '', ssn: '',
        addressLine1: '', addressLine2: '', city: '', state: '', zipCode: '',
        homePhone: '', cellPhone: '', fax: '', emergencyContactFirstName: '', emergencyContactLastName: '', emergencyContactRelation: '', emergencyContactPhone: '', emergencyContactAddress: '', email: '',
        gender: 'Male', dateOfBirth: '', twoWayAuth: 'Enabled', status: 'Active',
        timeZone: '', rpmStartDate: '', provider: '', systemCaregiverIds: [], practiceCaregiverIds: [],
        accountNumber: ''
    });

    const knownCaregiverObjects = useMemo(() => {
        const p = patientData || {};
        return [
            ...(Array.isArray(p.practice_caregivers) ? p.practice_caregivers : []),
            ...(Array.isArray(p.practiceCaregivers) ? p.practiceCaregivers : []),
            ...(Array.isArray(p.caregivers) ? p.caregivers : []),
            p.practice_caregiver,
            p.practiceCaregiver,
            p.caregiver,
            p.caregiver_id && p.caregiver_name ? { id: p.caregiver_id, name: p.caregiver_name } : null
        ].filter(Boolean);
    }, [patientData]);

    const knownSystemCaregiverObjects = useMemo(() => {
        const p = patientData || {};
        return [
            ...(Array.isArray(p.system_caregivers) ? p.system_caregivers : []),
            ...(Array.isArray(p.systemCaregivers) ? p.systemCaregivers : []),
            p.system_caregiver,
            p.systemCaregiver
        ].filter(Boolean);
    }, [patientData]);

    const knownProviderObjects = useMemo(() => {
        const p = patientData || {};
        return [
            ...(Array.isArray(p.providers) ? p.providers : []),
            p.provider,
            p.provider_id && p.provider_name ? { id: p.provider_id, name: p.provider_name } : null
        ].filter(Boolean);
    }, [patientData]);

    const systemCaregiverPickerOptions = useMemo(
        () => buildCaregiverOptionsWithStubs(systemCaregivers, formData.systemCaregiverIds, knownSystemCaregiverObjects),
        [systemCaregivers, formData.systemCaregiverIds, knownSystemCaregiverObjects]
    );
    const practiceCaregiverPickerOptions = useMemo(
        () => buildCaregiverOptionsWithStubs(practiceCaregivers, formData.practiceCaregiverIds, knownCaregiverObjects),
        [practiceCaregivers, formData.practiceCaregiverIds, knownCaregiverObjects]
    );
    const providerPickerOptions = useMemo(
        () => buildCaregiverOptionsWithStubs(practiceProviders, formData.provider ? [formData.provider] : [], knownProviderObjects),
        [practiceProviders, formData.provider, knownProviderObjects]
    );

    const [fieldErrors, setFieldErrors] = useState({});
    const [initialEditSnapshot, setInitialEditSnapshot] = useState(null);

    const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '', ssn: '' });

    const excludeUserId = patientData?.user_id ?? patientData?.userId ?? null;
    const excludePatientId = patientData?.id ?? patientData?.patient_id ?? patientId ?? null;
    const {
        scheduleUsernameCheck,
        scheduleEmailCheck,
        scheduleSsnCheck,
        handleUsernamePaste,
        handleUsernameBlur,
        handleEmailPaste,
        ensureUsernameAvailable,
        ensureEmailAvailable,
        ensureSsnAvailable,
        checking,
    } = useUsernameEmailAvailability({
        excludeUserId,
        excludePatientId,
        originalUsername: loadedCredentials.username,
        originalEmail: loadedCredentials.email,
        originalSsn: loadedCredentials.ssn,
    });

    // Form State - Monitoring & Insurance (Part of Tab 1)
    const [monitoringTypes, setMonitoringTypes] = useState({ bloodPressure: false, bloodGlucose: false, weight: false });
    const [monitoringValues, setMonitoringValues] = useState({ bloodPressure: '', bloodGlucose: '', weight: '' });
    const [icdCodesByMeasureType, setIcdCodesByMeasureType] = useState({ 1: [], 2: [], 3: [] });
    const [practiceMonitoringTypes, setPracticeMonitoringTypes] = useState([]);
    const [requiresSystemCaregiver, setRequiresSystemCaregiver] = useState(true);
    const [insuranceData, setInsuranceData] = useState({
        primary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' },
        secondary: { insuranceName: '', subscriberId: '', insuredName: '', groupNo: '' }
    });

    const isChanged = useMemo(() => {
        if (!initialEditSnapshot) return true;
        if (pendingDocumentCount > 0) return true;
        const current = JSON.stringify({
            formData,
            monitoringTypes,
            monitoringValues,
            insuranceData,
        });
        return current !== initialEditSnapshot;
    }, [formData, monitoringTypes, monitoringValues, insuranceData, initialEditSnapshot, pendingDocumentCount]);

    // Form State - Tab 3: Manage Meter
    const [meterForm, setMeterForm] = useState({ modelNumber: '', extension: '', serialNumber: '', gatewaySerialNumber: '' });

    // Form State - Tab 5: Programs
    const [patientPrograms, setPatientPrograms] = useState([]);
    const [programConfirmModal, setProgramConfirmModal] = useState({ isOpen: false, program: null });
    const [rpmEnrollModal, setRpmEnrollModal] = useState({ isOpen: false, program: null, rpmStartDate: '' });

    const [patientsLogModule, setPatientsLogModule] = useState(LOG_MODULE_PATIENT);
    const [patientsLogSection, setPatientsLogSection] = useState(null);
    const [previousTab, setPreviousTab] = useState('patient-info');

    const prevActiveTabRef = useRef(null);

    const openPatientsLog = (moduleId, section = null) => {
        setPatientsLogModule(moduleId);
        setPatientsLogSection(section);
        setPreviousTab(activeTab);
        setActiveTab('event-logs');
    };

    useEffect(() => {
        prevActiveTabRef.current = null;
    }, [patientId]);

    useEffect(() => {
        lookupService.getIcdCodes()
            .then((res) => {
                if (res?.success && Array.isArray(res.data)) {
                    setIcdCodesByMeasureType(groupIcdCodesByMeasureType(res.data));
                }
            })
            .catch(() => {
                setIcdCodesByMeasureType({ 1: [], 2: [], 3: [] });
            });
    }, []);

    useEffect(() => {
        loadPatientInformationCore();
        // location.key: refetch when returning to this screen (parity with AddPatient edit load).
    }, [practiceId, patientId, location.key]);

    // Whenever user returns to Patient Info from another tab, reload time zone from the server.
    useEffect(() => {
        const prev = prevActiveTabRef.current;
        prevActiveTabRef.current = activeTab;
        if (activeTab !== 'patient-info' || !practiceId || !patientId) return;
        const switchedFromOtherTab = prev != null && prev !== 'patient-info';
        if (!switchedFromOtherTab) return;

        let cancelled = false;
        (async () => {
            try {
                const pRes = await patientService.getPatient(practiceId, patientId);
                if (cancelled || !pRes.data?.success) return;
                const raw = pRes.data.data?.time_zone ?? pRes.data.data?.timeZone;
                const tz = normalizeTimeZone(raw);
                setFormData((prevForm) => ({ ...prevForm, timeZone: tz }));
            } catch {
                /* ignore */
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, practiceId, patientId]);

    useEffect(() => () => clearBreadcrumbs(), [clearBreadcrumbs]);

    useEffect(() => {
        if (loading || !patientData) return;
        const practiceName = patientData.practice_name || 'Practice';
        const patientCrumbName = `${patientData.last_name || ''}${patientData.last_name ? ', ' : ''}${patientData.first_name || ''}`.trim() || 'Patient';

        const fromDashboard = isFromPracticeDashboard(location);
        const activeProgram = location.state?.activeProgram;
        const rootCrumb = { label: 'Dashboard', path: '/dashboard' };

        // Dashboard → [Practice Name] → Patients → [Patient Name] → Edit patient
        updateBreadcrumbs([
            rootCrumb,
            { label: practiceName, path: `/practices/${practiceId}`, state: { fromDashboard } },
            {
              label: 'Patients',
              path: buildPracticePatientTabPath(practiceId, { fromDashboard, program: activeProgram }),
              state: { fromDashboard }
            },

            { label: patientCrumbName, path: `/practices/${practiceId}/patients/${patientId}`, state: { fromDashboard, activeProgram } },
            { label: 'Edit patient', path: null, active: true }
        ]);
    }, [loading, patientData, practiceId, patientId, updateBreadcrumbs, location]);



    // Meter mapping tab: load product dropdown (meters list comes from initial patient load + local adds)
    useEffect(() => {
        if (!practiceId || !patientId || activeTab !== 'meter-mapping') return;

        let cancelled = false;
        (async () => {
            try {
                const productsRes = await inventoryService.getProductsForDropdown();
                if (cancelled) return;
                setProducts(normalizeProductsDropdownResponse(productsRes));
            } catch (e) {
                console.error('Meter tab products load failed:', e);
                toast.error('Failed to load products for meter mapping');
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, practiceId, patientId]);

    // Programs tab logic
    useEffect(() => {
        if (!practiceId || !patientId || activeTab !== 'programs') return;
        let cancelled = false;
        (async () => {
            try {
                const res = await patientService.getPatientPrograms(practiceId, patientId);
                if (cancelled) return;
                if (res.data.success) {
                    setPatientPrograms(sortCareProgramsForDisplay(res.data.data.programs || []));
                }
            } catch (err) {
                toast.error('Failed to load patient programs');
            }
        })();
        return () => { cancelled = true; };
    }, [activeTab, practiceId, patientId]);

    const patientProfilePicUrl = useMemo(
        () => buildPatientProfileImageUrl(patientData?.profile_pic),
        [patientData?.profile_pic]
    );

    const practiceLogoUrl = useMemo(() => {
        const raw = patientData?.practice_logo;
        if (!raw) return null;
        if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
        const baseUrl = API_URL.replace('/api', '');
        const logoPath = raw.startsWith('/') ? raw : (raw.startsWith('uploads') ? `/${raw}` : `/uploads/practice-logos/${raw}`);
        return `${baseUrl}${logoPath}`;
    }, [patientData?.practice_logo]);

    const [practiceLogoFailed, setPracticeLogoFailed] = useState(false);
    useEffect(() => {
        setPracticeLogoFailed(false);
    }, [practiceLogoUrl]);

    const loadPatientInformationCore = async () => {
        try {
            setLoading(true);
            const [pRes, providersRes, caregiversRes, sysRes, metersRes, productsRes, practiceRes] = await Promise.all([
                patientService.getPatient(practiceId, patientId),
                practiceService.getPracticeProviders(practiceId),
                practiceService.getPracticeCaregivers(practiceId),
                practiceService.getSystemCaregivers(practiceId),
                patientService.getPatientMeters(practiceId, patientId).catch(() => ({ data: { data: [] } })),
                inventoryService.getProductsForDropdown().catch(() => ({ data: [] })),
                practiceService.getPractice(practiceId).catch(() => ({ data: { data: {} } }))
            ]);

            if (pRes.data.success) {
                const p = pRes.data.data;
                setPatientData(p);

                // Populate formData
                // Map status to form value (Active, Pending, Locked)
                const statusValue = patientStatusFormValue(p.status ?? p.patient_status ?? p.status_id);

                // Map gender — backend already converts to string in getPatient, but handle edge cases
                let genderValue = p.gender || 'Male';
                if (p.gender === 1) genderValue = 'Male';
                else if (p.gender === 2) genderValue = 'Female';
                else if (p.gender === 3) genderValue = 'Other';

                const rawHome = p.cellPhone || p.cell_phone || p.cell_phone_number || '';
                const rawCell = p.homePhone || p.phone || p.phone_number || '';
                const homeDigits = rawHome ? normalizePhoneDigits(rawHome) : '';
                const cellDigits = rawCell ? normalizePhoneDigits(rawCell) : '';

                const titleFromApi = (() => {
                    const tid = p.title_id ?? p.title;
                    if (tid == null || tid === '') return '';
                    const n = Number(tid);
                    if (Number.isFinite(n) && n > 0) {
                        const titleMap = { 1: 'Dr.', 2: 'Mr.', 3: 'Ms.', 4: 'Mrs.' };
                        return titleMap[n] || String(n);
                    }
                    return String(tid);
                })();
                const middleFromApi = (p.middle_name != null && p.middle_name !== '')
                    ? String(p.middle_name).trim().slice(0, 1)
                    : '';

                const loadedFormData = {
                    title: titleFromApi,
                    firstName: p.first_name || '',
                    middleInitial: middleFromApi,
                    lastName: p.last_name || '',
                    username: p.username || '',
                    addressLine1: p.address_line_1 || p.address?.address_line_1 || '',
                    addressLine2: p.address_line_2 || p.address?.address_line_2 || '',
                    city: p.city || p.address?.city || '',
                    state: stateSelectValueFromPatient(p),
                    zipCode: p.zip_code != null || p.address?.zip_code != null ? String(p.zip_code ?? p.address?.zip_code ?? '') : '',
                    ssn: p.ssn || '',
                    homePhone: homeDigits ? formatPhoneDisplay(homeDigits) : '',
                    cellPhone: cellDigits ? formatPhoneDisplay(cellDigits) : '',
                    fax: p.fax ? formatPhoneDisplay(p.fax) : '',
                    emergencyContactFirstName: p.emergency_contact_first_name || p.emergencyContactFirstName || '',
                    emergencyContactLastName: p.emergency_contact_last_name || p.emergencyContactLastName || '',
                    emergencyContactRelation: p.emergency_contact_relation || p.emergencyContactRelation || '',
                    emergencyContactPhone: (p.emergency_contact_phone || p.emergencyContactPhone)
                        ? formatPhoneDisplay(p.emergency_contact_phone || p.emergencyContactPhone)
                        : '',
                    emergencyContactAddress: p.emergency_contact_address || p.emergencyContactAddress || '',
                    email: p.email || '',
                    gender: genderValue,
                    dateOfBirth: p.date_of_birth ? p.date_of_birth.split('T')[0] : '',
                    twoWayAuth: Number(p.is_two_way_auth ?? p.two_way_auth ?? p.twoWayAuth ?? p.user?.is_two_way_auth) === 1 ? 'Enabled' : 'Disabled',
                    status: statusValue,
                    timeZone: normalizeTimeZone(p.time_zone ?? p.timeZone),
                    rpmStartDate: p.rpm_start_date ? p.rpm_start_date.split('T')[0] : '',
                    provider: p.provider_id ? String(p.provider_id) : '',
                    systemCaregiverIds: expandCaregiverIdsFromApi(
                        p.system_caregiver_ids || p.systemCaregiverIds,
                        p.system_caregiver_id ?? p.system_caregiver?.id
                    ),
                    practiceCaregiverIds: (() => {
                        const fromJunction = expandCaregiverIdsFromApi(
                            p.practice_caregiver_ids || p.practiceCaregiverIds,
                            null
                        );
                        const fromColumn = expandCaregiverIdsFromApi(null, p.caregiver_id);
                        const seen = new Set();
                        const out = [];
                        for (const id of [...fromJunction, ...fromColumn]) {
                            if (!id || seen.has(id)) continue;
                            seen.add(id);
                            out.push(String(id));
                        }
                        if (out.length > 0) return out;
                        const one = p.practice_caregiver_id ?? p.practiceCaregiver?.id;
                        return one != null && String(one).trim() !== '' ? [String(one)] : [];
                    })(),
                    accountNumber: p.id ? String(p.id) : (p.patient_id || p.account_number || '')
                };

                setFormData(loadedFormData);
                setLoadedCredentials({
                    username: loadedFormData.username || '',
                    email: loadedFormData.email || '',
                    ssn: loadedFormData.ssn || '',
                });

                // Populate Monitoring — handle both object and array formats
                const mTypes = p.monitoring_types || p.monitoringTypes || {};
                const mVals = p.monitoring_values || p.monitoringValues || {};
                const newTypes = { bloodPressure: false, bloodGlucose: false, weight: false };
                const newValues = { bloodPressure: '', bloodGlucose: '', weight: '' };

                if (Array.isArray(mTypes)) {
                    // Array format: [{type: 'bloodPressure', icdCode: '...'}, ...] or ["Blood Pressure", ...]
                    mTypes.forEach(m => {
                        if (typeof m === 'string' || typeof m === 'number') {
                            const key = String(m).trim().toLowerCase();
                            if (key === 'bloodpressure' || key === 'blood pressure' || key === 'bp' || key === '1') {
                                newTypes.bloodPressure = true;
                            } else if (key === 'bloodglucose' || key === 'blood glucose' || key === 'bg' || key === '2') {
                                newTypes.bloodGlucose = true;
                            } else if (key === 'weight' || key === 'wt' || key === '3') {
                                newTypes.weight = true;
                            }
                            return;
                        }
                        if (m.type === 'bloodPressure' || m.type === 1 || m.type === 'bp') { newTypes.bloodPressure = true; newValues.bloodPressure = m.icdCode || m.icd_code || ''; }
                        if (m.type === 'bloodGlucose' || m.type === 2 || m.type === 'bg') { newTypes.bloodGlucose = true; newValues.bloodGlucose = m.icdCode || m.icd_code || ''; }
                        if (m.type === 'weight' || m.type === 3 || m.type === 'wt') { newTypes.weight = true; newValues.weight = m.icdCode || m.icd_code || ''; }
                    });
                } else if (typeof mTypes === 'object') {
                    // Object format: { bloodPressure: true/false, bloodGlucose: true/false, weight: true/false }
                    newTypes.bloodPressure = !!mTypes.bloodPressure;
                    newTypes.bloodGlucose = !!mTypes.bloodGlucose;
                    newTypes.weight = !!mTypes.weight;
                    // Populate ICD values from monitoring_values
                    if (mVals.bloodPressure) newValues.bloodPressure = mVals.bloodPressure;
                    if (mVals.bloodGlucose) newValues.bloodGlucose = mVals.bloodGlucose;
                    if (mVals.weight) newValues.weight = mVals.weight;
                }
                setMonitoringTypes(newTypes);
                setMonitoringValues(newValues);

                // Insurance from `insurances` table (via insurance_details / insurance_data on API)
                const ins = p.insurance_details || p.insurance_data || p.insuranceDetails || {};
                const loadedInsuranceData = {
                    primary: {
                        insuranceName: sanitizeInsuranceField('insuranceName', ins.primary?.insuranceName || ins.primary?.insurance_name || ''),
                        subscriberId: sanitizeInsuranceField('subscriberId', ins.primary?.subscriberId || ins.primary?.subscriber_id || ''),
                        insuredName: sanitizeInsuranceField('insuredName', ins.primary?.insuredName || ins.primary?.insured_name || ''),
                        groupNo: sanitizeInsuranceField('groupNo', ins.primary?.groupNo || ins.primary?.group_no || '')
                    },
                    secondary: {
                        insuranceName: sanitizeInsuranceField('insuranceName', ins.secondary?.insuranceName || ins.secondary?.insurance_name || ''),
                        subscriberId: sanitizeInsuranceField('subscriberId', ins.secondary?.subscriberId || ins.secondary?.subscriber_id || ''),
                        insuredName: sanitizeInsuranceField('insuredName', ins.secondary?.insuredName || ins.secondary?.insured_name || ''),
                        groupNo: sanitizeInsuranceField('groupNo', ins.secondary?.groupNo || ins.secondary?.group_no || '')
                    }
                };
                setInsuranceData(loadedInsuranceData);

                setInitialEditSnapshot(JSON.stringify({
                    formData: loadedFormData,
                    monitoringTypes: newTypes,
                    monitoringValues: newValues,
                    insuranceData: loadedInsuranceData,
                }));
            } else {
                toast.error(pRes.data?.message || 'Patient not found');
                setPatientData(null);
            }

            // Extract data correctly from provider/caregiver API responses
            const providersData = Array.isArray(providersRes.data?.data) ? providersRes.data.data
                : Array.isArray(providersRes.data) ? providersRes.data : [];
            const caregiversData = Array.isArray(caregiversRes.data?.data) ? caregiversRes.data.data
                : Array.isArray(caregiversRes.data) ? caregiversRes.data : [];
            const sysRaw = sysRes.data?.data;
            const sysCaregiversData = Array.isArray(sysRaw?.caregivers)
                ? sysRaw.caregivers
                : Array.isArray(sysRaw)
                    ? sysRaw
                    : Array.isArray(sysRes.data?.caregivers)
                        ? sysRes.data.caregivers
                        : [];

            setPracticeProviders(providersData);
            setPracticeCaregivers(caregiversData);
            setSystemCaregivers(sysCaregiversData);

            const rawMeters = metersRes?.data?.data || [];
            setMappedDevices(normalizeMappedDeviceRows(rawMeters));
            setProducts(normalizeProductsDropdownResponse(productsRes));

            const practiceInfo = practiceRes?.data?.data || {};
            setPracticeMonitoringTypes(normalizePracticeMonitoringTypes(practiceInfo.selected_monitoring_types));
            setRequiresSystemCaregiver(isEvitalsMonitoringPractice(practiceInfo));
        } catch (err) {
            console.error('Error fetching data:', err);
            toast.error('Failed to load patient data');
        } finally {
            setLoading(false);
        }
    };

    const formatPhoneNumber = formatUSPhoneNumber;

    const formatSSN = (value) => {
        if (!value) return value;
        const ssn = value.replace(/[^\d]/g, '');
        const ssnLength = ssn.length;
        if (ssnLength < 4) return ssn;
        if (ssnLength < 6) {
            return `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
        }
        return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        clearFieldError(setFieldErrors, name);

        // Phone Number Masking
        if (['homePhone', 'cellPhone', 'fax', 'emergencyContactPhone'].includes(name)) {
            const formatted = value;
            const phoneDigits = extractPhoneDigits(formatted);
            setFormData((prev) => ({ ...prev, [name]: formatted }));
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                const typingError = getPhoneTypingError(phoneDigits, {
                    required: name === 'cellPhone',
                    requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : name === 'emergencyContactPhone' ? 'Emergency Contact Phone' : 'Fax',
                });
                if (typingError) next[name] = typingError;
                return next;
            });
            return;
        }

        if (name === 'status') {
            const initialStatusId = normalizePatientStatusId(patientData?.status ?? patientData?.patient_status);
            const targetStatusId = normalizePatientStatusId(value);
            const hasAssignedDevices = mappedDevices.some((m) => {
                const sn = String(m.serial_number || m.serialNumber || '').trim();
                const mn = String(m.model_number || m.modelNumber || '').trim();
                return Boolean(sn && mn);
            });

            if (initialStatusId === 2 && (targetStatusId === 3 || targetStatusId === 4) && hasAssignedDevices) {
                setStatusDeviceRemovalModal({
                    show: true,
                    targetStatus: value
                });
                return;
            }
        }

        let processedValue = value;

        // No special characters for names and city
        if (['firstName', 'lastName', 'middleInitial', 'city'].includes(name)) {
            processedValue = value.replace(/[^a-zA-Z\s]/g, '');
        }

        // Address lines - allow alphanumeric and basic punctuation like spaces, hash, comma, hyphen
        if (['addressLine1', 'addressLine2'].includes(name)) {
            processedValue = value.replace(/[^a-zA-Z0-9\s#,.-]/g, '');
        }

        // Username: letters, numbers, period, underscore (max length enforced)
        if (name === 'username') {
            processedValue = sanitizeUsernameInput(value);
        }

        // SSN Masking
        if (name === 'ssn') {
            const numericValue = value.replace(/\D/g, '');
            if (numericValue.length <= 9) {
                processedValue = formatSSN(numericValue);
            } else {
                return;
            }
        }

        if (name === 'zipCode') {
            const numericValue = value.replace(/\D/g, '');
            if (numericValue.length <= 5) {
                processedValue = numericValue;
            } else {
                return;
            }
        }

        setFormData(prev => ({ ...prev, [name]: processedValue }));
        setFieldErrors(prev => {
            const next = { ...prev };
            delete next[name];

            if (name === 'username') {
                const v = String(processedValue || '').trim();
                if (!v) next.username = 'Username is required';
                else {
                    const formatError = getUsernameFormatError(v);
                    if (formatError) next.username = formatError;
                }
            }
            if (name === 'email') {
                const v = String(value || '').trim();
                if (!v) next.email = 'Email is required';
                else if (!isValidEmailFormat(v)) next.email = 'Invalid email format';
            }
            if (name === 'middleInitial' && value.length > 20) {
                return;
            }
            if (name === 'city') {
                const v = String(value || '').trim();
                if (v && !/^[a-zA-Z\s'-]+$/.test(v)) next.city = 'City may only contain letters, spaces, hyphens, and apostrophes';
            }
            if (name === 'zipCode') {
                const v = String(processedValue || '').trim();
                if (v && v.length < 5) next.zipCode = 'Zip code must be exactly 5 digits';
            }
            if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
                const digits = normalizePhoneDigits(processedValue);
                const typingError = getPhoneTypingError(digits, {
                    required: name === 'cellPhone',
                    requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : 'Fax',
                });
                if (typingError) next[name] = typingError;
                else delete next[name];
            }
            if (['timeZone', 'state', 'provider', 'practiceCaregiver', 'zipCode', 'addressLine1', 'city', 'firstName', 'lastName'].includes(name)) {
                if (String(processedValue ?? '').trim()) {
                    delete next[name];
                }
            }
            return next;
        });

        if (name === 'username') {
            const v = String(processedValue || '').trim();
            if (v && !getUsernameFormatError(v)) {
                scheduleUsernameCheck(v, setFieldErrors);
            }
        }
        if (name === 'email') {
            const v = String(processedValue || '').trim();
            if (v && isValidEmailFormat(v)) {
                scheduleEmailCheck(v, setFieldErrors);
            }
        }
        if (name === 'ssn') {
            scheduleSsnCheck(processedValue, setFieldErrors);
        }
    };

    const pickProviderField = (name, idStr) => {
        setFormData(prev => ({ ...prev, [name]: idStr }));
        if (name === 'provider') {
            setFieldErrors(prev => {
                if (!prev.provider) return prev;
                const next = { ...prev };
                delete next.provider;
                return next;
            });
        }
    };

    const addSystemCaregiverId = (idStr) => {
        setFormData(prev => {
            if (prev.systemCaregiverIds.includes(idStr)) return prev;
            return { ...prev, systemCaregiverIds: [...prev.systemCaregiverIds, idStr] };
        });
        setFieldErrors(prev => {
            if (!prev.systemCaregiver) return prev;
            const next = { ...prev };
            delete next.systemCaregiver;
            return next;
        });
    };

    const removeSystemCaregiverId = (idStr) => {
        setFormData(prev => ({
            ...prev,
            systemCaregiverIds: prev.systemCaregiverIds.filter((id) => id !== idStr)
        }));
    };

    const addPracticeCaregiverId = (idStr) => {
        setFormData(prev => {
            if (prev.practiceCaregiverIds.includes(idStr)) return prev;
            return { ...prev, practiceCaregiverIds: [...prev.practiceCaregiverIds, idStr] };
        });
        setFieldErrors(prev => {
            if (!prev.practiceCaregiver) return prev;
            const next = { ...prev };
            delete next.practiceCaregiver;
            return next;
        });
    };

    const removePracticeCaregiverId = (idStr) => {
        setFormData(prev => ({
            ...prev,
            practiceCaregiverIds: prev.practiceCaregiverIds.filter((id) => id !== idStr)
        }));
    };

    const handleInsuranceChange = (type, field, value) => {
        const sanitizedValue = sanitizeInsuranceField(field, value);
        setInsuranceData(prev => ({
            ...prev,
            [type]: { ...prev[type], [field]: sanitizedValue }
        }));
        const errKey = insuranceErrorKey(type, field);
        const liveError = getInsuranceFieldError(field, sanitizedValue);
        setFieldErrors(prev => {
            const updated = { ...prev };
            if (liveError) updated[errKey] = liveError;
            else delete updated[errKey];
            return updated;
        });
    };

    const handleInsuranceBlur = (type, field) => {
        const data = insuranceData[type] || {};
        const value = data[field] || '';
        const errKey = insuranceErrorKey(type, field);
        const liveError = getInsuranceFieldError(field, value);
        setFieldErrors(prev => {
            const updated = { ...prev };
            if (liveError) updated[errKey] = liveError;
            else delete updated[errKey];
            return updated;
        });
    };

    const handleUpdatePatient = async (e) => {
        e.preventDefault();
        const nextErr = {};
        if (!String(formData.firstName || '').trim()) nextErr.firstName = 'First Name is required';
        if (!String(formData.lastName || '').trim()) nextErr.lastName = 'Last Name is required';
        if (!String(formData.username || '').trim()) nextErr.username = 'Username is required';
        else {
            const formatError = getUsernameFormatError(formData.username);
            if (formatError) nextErr.username = formatError;
        }
        if (!String(formData.addressLine1 || '').trim()) nextErr.addressLine1 = 'Address Line 1 is required';
        const emailValue = String(formData.email || '').trim();
        if (!emailValue) nextErr.email = 'Email is required';
        else if (!isValidEmailFormat(emailValue)) nextErr.email = 'Invalid email format';
        const cityTrim = String(formData.city || '').trim();
        if (!cityTrim) nextErr.city = 'City is required';
        else if (!/^[a-zA-Z\s'-]+$/.test(cityTrim)) nextErr.city = 'City may only contain letters, spaces, hyphens, and apostrophes';
        if (!String(formData.state || '').trim()) nextErr.state = 'State is required';
        const zipStr = String(formData.zipCode || '').trim();
        if (!zipStr) nextErr.zipCode = 'Zip Code is required';
        else if (zipStr.length !== 5) nextErr.zipCode = 'Zip code must be exactly 5 digits';
        const homePhoneError = validateRequiredPhone(formData.homePhone, { required: false, label: 'Home Phone' });
        if (homePhoneError) nextErr.homePhone = homePhoneError;

        const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
        if (cellPhoneError) nextErr.cellPhone = cellPhoneError;

        const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
        if (faxError) nextErr.fax = faxError;
        const emergencyPhoneError = validateRequiredPhone(formData.emergencyContactPhone, { required: false, label: 'Emergency Contact Phone' });
        if (emergencyPhoneError) nextErr.emergencyContactPhone = emergencyPhoneError;
        if (!String(formData.timeZone || '').trim()) nextErr.timeZone = 'Time Zone is required';
        if (!String(formData.rpmStartDate || '').trim()) nextErr.rpmStartDate = 'RPM Start Date is required';
        if (!formData.provider || String(formData.provider).trim() === '') {
            nextErr.provider = 'Provider is required';
        }
        if (requiresSystemCaregiver && (!formData.systemCaregiverIds || formData.systemCaregiverIds.length === 0)) {
            nextErr.systemCaregiver = 'Select at least one system caregiver';
        }
        if (!formData.practiceCaregiverIds || formData.practiceCaregiverIds.length === 0) {
            nextErr.practiceCaregiver = 'Select at least one practice caregiver';
        }

        // Health Insurance Validation
        Object.assign(nextErr, validateInsuranceData(insuranceData));

        // Email Validation
        if (formData.email && /\s/.test(formData.email)) {
            nextErr.email = 'email id is invalid';
        }
        // Device Serial Number Validation
        const meterValidationError = validateMeterMappingList(mappedDevices);
        if (meterValidationError) {
            toast.error(meterValidationError);
            return;
        }

        const mergedErrors = preserveAvailabilityErrors(fieldErrors, nextErr);
        if (Object.keys(mergedErrors).length > 0) {
            setFieldErrors(mergedErrors);
            toast.error('Please fix the highlighted fields');
            return;
        }

        if (checking.username || checking.email || checking.ssn) {
            toast.warn('Please wait while username, email, and SSN availability are verified.');
            return;
        }

        const usernameOk = await ensureUsernameAvailable(formData.username, setFieldErrors);
        const emailOk = await ensureEmailAvailable(formData.email, setFieldErrors);
        const ssnOk = await ensureSsnAvailable(formData.ssn, setFieldErrors);
        if (!usernameOk || !emailOk || !ssnOk) {
            toast.error('Please fix the highlighted fields');
            return;
        }

        setFieldErrors({});
        setIsSubmitting(true);
        try {
            const data = new FormData();
            const systemCaregiverIdsNum = requiresSystemCaregiver
                ? formData.systemCaregiverIds
                    .map((id) => parseInt(id, 10))
                    .filter((n) => !Number.isNaN(n) && n > 0)
                : [];
            const practiceCaregiverIdsNum = formData.practiceCaregiverIds
                .map((id) => parseInt(id, 10))
                .filter((n) => !Number.isNaN(n) && n > 0);
            // Map frontend field names to backend-expected field names
            data.append('firstName', formData.firstName);
            data.append('middleName', formData.middleInitial);
            data.append('lastName', formData.lastName);
            data.append('username', formData.username);
            data.append('email', formData.email);
            data.append('title', formData.title);
            data.append('gender', formData.gender);
            data.append('dateOfBirth', formData.dateOfBirth);
            data.append('homePhone', formData.cellPhone);
            data.append('cellPhone', formData.homePhone);
            data.append('fax', formData.fax);
            data.append('emergencyContactFirstName', formData.emergencyContactFirstName || '');
            data.append('emergencyContactLastName', formData.emergencyContactLastName || '');
            data.append('emergencyContactRelation', formData.emergencyContactRelation || '');
            data.append('emergencyContactPhone', formData.emergencyContactPhone || '');
            data.append('emergencyContactAddress', formData.emergencyContactAddress || '');
            data.append('addressLine1', formData.addressLine1);
            data.append('addressLine2', formData.addressLine2);
            data.append('city', formData.city);
            const resolvedStateId = STATE_ABBR_TO_ID[formData.state] ?? parseInt(formData.state, 10);
            if (Number.isFinite(resolvedStateId) && resolvedStateId > 0) {
                data.append('state_id', String(resolvedStateId));
            }
            data.append('state', formData.state);
            data.append('zipCode', formData.zipCode);
            data.append('status', formData.status);
            data.append('timeZone', formData.timeZone);
            data.append('rpmStartDate', formData.rpmStartDate);
            data.append('provider', formData.provider);
            data.append('practice_caregiver_id', practiceCaregiverIdsNum.length > 0 ? String(practiceCaregiverIdsNum[0]) : '');
            data.append('practice_caregiver_ids', JSON.stringify(practiceCaregiverIdsNum));
            data.append('practiceCaregiverIds', JSON.stringify(practiceCaregiverIdsNum));
            data.append('system_caregiver_id', systemCaregiverIdsNum.length > 0 ? String(systemCaregiverIdsNum[0]) : '');
            data.append('system_caregiver_ids', JSON.stringify(systemCaregiverIdsNum));
            data.append('systemCaregiverIds', JSON.stringify(systemCaregiverIdsNum));
            data.append('twoWayAuth', formData.twoWayAuth === 'Enabled' ? '1' : '0');
            data.append('is_two_way_auth', formData.twoWayAuth === 'Enabled' ? '1' : '0');

            const monitoringTypesArray = [];
            if (monitoringTypes.bloodPressure) {
                monitoringTypesArray.push({ type: 'bloodPressure', icdCode: monitoringValues.bloodPressure || null });
            }
            if (monitoringTypes.bloodGlucose) {
                monitoringTypesArray.push({
                    type: 'bloodGlucose',
                    icdCode: monitoringValues.bloodGlucose && String(monitoringValues.bloodGlucose).trim() !== ''
                        ? String(monitoringValues.bloodGlucose).trim()
                        : null
                });
            }
            if (monitoringTypes.weight) {
                monitoringTypesArray.push({ type: 'weight', icdCode: monitoringValues.weight || null });
            }
            data.append('monitoring_types', JSON.stringify(monitoringTypesArray));
            data.append('monitoring_values', JSON.stringify(monitoringValues));
            data.append('insurance_data', JSON.stringify(insuranceData));

            const metersPayload = mappedDevicesToMeterMappingsPayload(mappedDevices);
            if (metersPayload.length > 0) {
                data.append('meter_mappings', JSON.stringify(metersPayload));
            }

            const res = await patientService.updatePatient(practiceId, patientId, data);
            if (res.data.success) {
                if (pendingDocumentCount > 0 && documentsSectionRef.current?.uploadPendingDocuments) {
                    const docResult = await documentsSectionRef.current.uploadPendingDocuments(patientId);
                    if (docResult?.failed > 0) {
                        toast.error(
                            docResult.uploaded > 0
                                ? `Patient updated, but ${docResult.failed} document(s) failed to upload.`
                                : 'Patient updated, but document upload failed.'
                        );
                    } else {
                        toast.success('Patient information updated successfully');
                    }
                } else {
                    toast.success('Patient information updated successfully');
                }
                setPatientData(prev => ({
                    ...prev,
                    status: formData.status,
                    patient_status: formData.status
                }));
                try {
                    const metersRefresh = await patientService.getPatientMeters(practiceId, patientId);
                    setMappedDevices(normalizeMappedDeviceRows(metersRefresh.data?.data || []));
                } catch {
                    /* ignore */
                }
            }
        } catch (err) {
            const raw = err.response?.data?.message || 'Update failed';
            toast.error(
              /foracare/i.test(raw)
                ? 'Unable to update the patient. Please check the device details and try again.'
                : raw
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddMeterToTable = () => {
        const modelNumber = String(meterForm.modelNumber || '').trim();
        const extension = String(meterForm.extension || '').trim();
        const serialNumber = String(meterForm.serialNumber || '').trim();
        if (!modelNumber || !extension || !serialNumber) {
            toast.error('Model, extension, and serial number are required');
            return;
        }
        const serialError = getDeviceSerialNumberError(serialNumber);
        if (serialError) {
            toast.error(serialError);
            return;
        }
        const duplicateSerial = mappedDevices.some(
            (m) => String(m.serial_number || '').trim().toLowerCase() === serialNumber.toLowerCase()
        );
        if (duplicateSerial) {
            toast.error('This serial number is already in the mapped devices list');
            return;
        }
        const row = {
            id: null,
            _localKey: `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            model_number: modelNumber,
            extension,
            serial_number: serialNumber,
            gateway_serial_number: String(meterForm.gatewaySerialNumber || '').trim()
        };
        setMappedDevices((prev) => [...prev, row]);
        setMeterForm({ modelNumber: '', extension: '', serialNumber: '', gatewaySerialNumber: '' });
        toast.success('Meter added to the list. Open Patient Info and click Update to save.');
    };

    const handleDeleteMeterRow = (row) => {
        if (!window.confirm('Remove this row from the list? Click Update on Patient Info to save changes.')) return;
        setMappedDevices((prev) =>
            prev.filter((m) => (row.id != null ? m.id !== row.id : m._localKey !== row._localKey))
        );
    };

    const handleEditMeterRow = (row) => {
        setMeterForm({
            modelNumber: row.model_number || '',
            extension: row.extension || '',
            serialNumber: row.serial_number || '',
            gatewaySerialNumber: row.gateway_serial_number || ''
        });
        setMappedDevices((prev) =>
            prev.filter((m) => (row.id != null ? m.id !== row.id : m._localKey !== row._localKey))
        );
        toast.info('Row loaded into form above. Make changes and click Add meter.');
    };

    const handleToggleProgram = (prog) => {
        if (prog.enrolled) {
            setProgramConfirmModal({ isOpen: true, program: prog });
        } else if (String(prog.short_name || '').toUpperCase() === 'RPM') {
            setRpmEnrollModal({
                isOpen: true,
                program: prog,
                rpmStartDate: formData.rpmStartDate || new Date().toISOString().slice(0, 10)
            });
        } else {
            const key = String(prog.short_name || '').trim().toUpperCase();
            if (key === 'PCM' || key === 'CCM') {
                const conditionCount = parseRawChronicConditionItems(
                    patientData?.chronic_conditions || patientData?.icd_codes
                ).length;
                const practiceHasPcm = patientPrograms.some((p) => String(p.short_name || '').toUpperCase() === 'PCM');
                const practiceHasCcm = patientPrograms.some((p) => String(p.short_name || '').toUpperCase() === 'CCM');
                const conflict = pcmCcmToggleConflictMessage(key.toLowerCase(), conditionCount, { practiceHasPcm, practiceHasCcm });
                if (conflict) {
                    toast.error(conflict);
                    return;
                }
                setPatientPrograms((prev) => prev.map((p) => {
                    const sn = String(p.short_name || '').toUpperCase();
                    if (p.id === prog.id) return { ...p, enrolled: true };
                    if ((key === 'PCM' && sn === 'CCM') || (key === 'CCM' && sn === 'PCM')) {
                        return { ...p, enrolled: false };
                    }
                    return p;
                }));
                return;
            }
            setPatientPrograms(prev => prev.map(p => p.id === prog.id ? { ...p, enrolled: true } : p));
        }
    };

    const handleConfirmRpmEnroll = async () => {
        const programToEnroll = rpmEnrollModal.program;
        const rpmStartDate = String(rpmEnrollModal.rpmStartDate || '').trim();
        if (!rpmStartDate) {
            toast.error('RPM Start Date is required');
            return;
        }
        if (new Date(rpmStartDate) > new Date()) {
            toast.error('RPM Start Date cannot be in the future');
            return;
        }

        const newPatientPrograms = patientPrograms.map(p =>
            p.id === programToEnroll.id ? { ...p, enrolled: true } : p
        );
        setPatientPrograms(newPatientPrograms);
        setRpmEnrollModal({ isOpen: false, program: null, rpmStartDate: '' });
        setFormData(prev => ({ ...prev, rpmStartDate }));

        try {
            const enrolled_programs = newPatientPrograms.filter(p => p.enrolled).map(p => p.short_name);
            const res = await patientService.updatePatientPrograms(practiceId, patientId, {
                enrolled_programs,
                rpm_start_date: rpmStartDate
            });
            if (res.data.success) {
                toast.success('RPM enrolled successfully');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to enroll RPM program');
            setPatientPrograms(patientPrograms);
            setFormData(prev => ({ ...prev, rpmStartDate: formData.rpmStartDate }));
        }
    };

    const handleConfirmDisenroll = async () => {
        const programToDisenroll = programConfirmModal.program;
        const newPatientPrograms = patientPrograms.map(p => p.id === programToDisenroll.id ? { ...p, enrolled: false } : p);
        setPatientPrograms(newPatientPrograms);
        setProgramConfirmModal({ isOpen: false, program: null });

        try {
            const enrolled_programs = newPatientPrograms.filter(p => p.enrolled).map(p => p.short_name);
            const res = await patientService.updatePatientPrograms(practiceId, patientId, { enrolled_programs });
            if (res.data.success) {
                toast.success(`${programToDisenroll.short_name} disenrolled successfully`);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to disenroll program');
            setPatientPrograms(patientPrograms); // Rollback on error
        }
    };

    const handleSavePrograms = async () => {
        try {
            const enrolled_programs = patientPrograms.filter(p => p.enrolled).map(p => p.short_name);
            const payload = { enrolled_programs };
            if (enrolled_programs.map(p => String(p).toUpperCase()).includes('RPM') && formData.rpmStartDate) {
                payload.rpm_start_date = formData.rpmStartDate;
            }
            const res = await patientService.updatePatientPrograms(practiceId, patientId, payload);
            if (res.data.success) {
                toast.success('Programs updated successfully');
                if (res.data.rpm_start_date) {
                    setFormData(prev => ({ ...prev, rpmStartDate: res.data.rpm_start_date }));
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update programs');
        }
    };

    if (loading) {
        return (
            <div className="p-edit-container p-edit-skeleton-page">
                <div className="p-edit-single-card">
                    <div className="p-edit-header p-edit-skeleton-header">
                        <div className="p-edit-skeleton-row">
                            <span className="p-edit-skeleton-avatar" />
                            <div className="p-edit-skeleton-col">
                                <span className="p-edit-skeleton-line w45" />
                                <span className="p-edit-skeleton-line w30" />
                            </div>
                        </div>
                        <span className="p-edit-skeleton-logo" />
                    </div>

                    <div className="p-edit-tabs p-edit-skeleton-tabs">
                        <span className="p-edit-skeleton-tab" />
                        <span className="p-edit-skeleton-tab" />
                        <span className="p-edit-skeleton-tab" />
                        <span className="p-edit-skeleton-tab" />
                    </div>

                    <div className="p-edit-content">
                        <div className="p-edit-skeleton-section">
                            <span className="p-edit-skeleton-line w25" />
                            <div className="p-edit-skeleton-form-grid">
                                {Array.from({ length: 10 }).map((_, i) => (
                                    <div key={`p-edit-skel-field-${i}`} className="p-edit-skeleton-field">
                                        <span className="p-edit-skeleton-line w40" />
                                        <span className="p-edit-skeleton-input" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-edit-skeleton-section">
                            <span className="p-edit-skeleton-line w20" />
                            <div className="p-edit-skeleton-table">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <span key={`p-edit-skel-row-${i}`} className="p-edit-skeleton-input" />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const patientDisplayName = `${patientData?.last_name || ''}${patientData?.last_name ? ', ' : ''}${patientData?.first_name || ''}`.trim() || 'Patient';

    return (
        <div className="p-edit-container">
            <div className="p-edit-single-card">
                <div className="p-edit-header">
                    <div className="p-edit-title-box">
                        <div className="d-flex align-items-center gap-3">
                            {patientProfilePicUrl && (
                                <div className="p-edit-profile-pic-wrap">
                                    <img
                                        src={patientProfilePicUrl}
                                        alt={patientDisplayName}
                                        className="p-edit-profile-pic-img"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            )}
                            <div>
                                <h2>{patientDisplayName}</h2>
                                <div className="d-flex align-items-center gap-3">
                                    <p className="mb-0">{patientData?.practice_name ? `Practice: ${patientData.practice_name}` : ''}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        {practiceLogoUrl && !practiceLogoFailed ? (
                            <div className="p-edit-header-logo-wrap">
                                <img
                                    src={practiceLogoUrl}
                                    alt={patientData?.practice_name ? `${patientData.practice_name} logo` : 'Practice logo'}
                                    className="p-edit-header-logo-img"
                                    onError={() => setPracticeLogoFailed(true)}
                                />
                            </div>
                        ) : (
                            <div className="p-edit-header-logo-placeholder" aria-hidden />
                        )}
                        {formData.accountNumber && (
                            <span style={{ color: 'var(--ev-navy)', fontSize: '14px', fontWeight: 600, backgroundColor: '#f0f4f8', padding: '4px 10px', borderRadius: '4px' }}>
                                Account #: {formData.accountNumber}
                            </span>
                        )}
                    </div>
                </div>

                <div className="p-edit-tabs">
                    <button type="button" className={`p-edit-tab ${activeTab === 'patient-info' ? 'active' : ''}`} onClick={() => setActiveTab('patient-info')}>
                        <FontAwesomeIcon icon={faPencilSquare} /> Patient Info
                    </button>
                    <button type="button" className={`p-edit-tab ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveTab('schedule')}>
                        <FontAwesomeIcon icon={faGear} /> Schedule & Targets
                    </button>
                    <button type="button" className={`p-edit-tab ${activeTab === 'meter-mapping' ? 'active' : ''}`} onClick={() => setActiveTab('meter-mapping')}>
                        <FontAwesomeIcon icon={faMicrochip} /> Meter Mapping
                    </button>
                    <button type="button" className={`p-edit-tab ${activeTab === 'uploaded-documents' ? 'active' : ''}`} onClick={() => setActiveTab('uploaded-documents')}>
                        <FontAwesomeIcon icon={faFileLines} /> Uploaded Documents
                    </button>
                    <button type="button" className={`p-edit-tab ${activeTab === 'programs' ? 'active' : ''}`} onClick={() => setActiveTab('programs')}>
                        <FontAwesomeIcon icon={faGear} /> Programs
                    </button>
                </div>

                <div className="p-edit-content">
                    {activeTab === 'event-logs' && (
                        <div className="p-edit-tab-pane">
                            <div className="d-flex align-items-center mb-4 pb-3" style={{ borderBottom: '1px solid #eee' }}>
                                <button type="button" className="p-edit-btn-event-log" onClick={() => setActiveTab(previousTab || 'patient-info')} style={{ width: 'auto', padding: '0 16px' }}>
                                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                                </button>
                                <h3 className="mb-0 ms-3" style={{ fontSize: '1.2rem', color: '#263651', fontWeight: 600 }}>
                                    Event Logs - {patientsLogModule}
                                    {formData.username ? ` (${formData.username})` : ''}
                                </h3>
                            </div>
                            <PatientsLogContent
                                show={true}
                                moduleId={patientsLogModule}
                                section={patientsLogSection}
                                patientId={patientId != null && String(patientId).trim() !== '' ? parseInt(String(patientId), 10) || patientId : null}
                                practiceId={practiceId != null && String(practiceId).trim() !== '' ? parseInt(String(practiceId), 10) || practiceId : null}
                                patientUsername={formData.username || patientData?.username || ''}
                                patientFirstName={formData.first_name || patientData?.first_name || ''}
                                patientLastName={formData.last_name || patientData?.last_name || ''}
                            />
                        </div>
                    )}

                    {activeTab === 'patient-info' && (
                        <div className="p-edit-tab-pane p-edit-patient-info-tab">
                            <div className="add-patient-form-card p-edit-patient-info-pane">
                                <form onSubmit={handleUpdatePatient} className="add-patient-provider-form">
                                    <h5 className="add-patient-block-title p-edit-block-title-first">Patient information</h5>
                                    <div className="add-patient-block p-edit-form-standard-layout">
                                        <div className="pif-demo-section-label">Personal Information</div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group" style={{ flex: '0 0 100px', minWidth: '100px' }}>
                                                <label htmlFor="title">Title</label>
                                                <select id="title" name="title" value={formData.title} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="">- Select -</option>
                                                    <option value="1">Dr.</option>
                                                    <option value="2">Mr.</option>
                                                    <option value="3">Mrs.</option>
                                                    <option value="4">Ms.</option>
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="firstName">First Name <span className="add-patient-required">*</span></label>
                                                <input id="firstName" type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" required className={`add-patient-form-control ${fieldErrors.firstName ? 'is-invalid' : ''}`} />
                                                {fieldErrors.firstName && <div className="add-patient-error-message">{fieldErrors.firstName}</div>}
                                            </div>
                                        </div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group" style={{ flex: '0 0 100px' }}>
                                                <label htmlFor="middleInitial">Middle Name</label>
                                                <input id="middleInitial" type="text" name="middleInitial" value={formData.middleInitial} onChange={handleInputChange} placeholder="e.g., Marie" className={`add-patient-form-control ${fieldErrors.middleInitial ? 'is-invalid' : ''}`} maxLength="20" />
                                                {fieldErrors.middleInitial && <div className="add-patient-error-message">{fieldErrors.middleInitial}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="lastName">Last Name <span className="add-patient-required">*</span></label>
                                                <input id="lastName" type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" required className={`add-patient-form-control ${fieldErrors.lastName ? 'is-invalid' : ''}`} />
                                                {fieldErrors.lastName && <div className="add-patient-error-message">{fieldErrors.lastName}</div>}
                                            </div>
                                        </div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="gender">Gender</label>
                                                <select id="gender" name="gender" value={formData.gender} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="dateOfBirth">Date of Birth</label>
                                                <AppDateInput id="dateOfBirth" name="dateOfBirth" value={formData.dateOfBirth} onChange={(v) => handleInputChange({ target: { name: 'dateOfBirth', value: v } })} className="add-patient-form-control" />
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="ssn">SSN</label>
                                                <input
                                                    id="ssn"
                                                    type="text"
                                                    name="ssn"
                                                    value={formData.ssn}
                                                    onChange={handleInputChange}
                                                    onBlur={() => {
                                                        const digits = String(formData.ssn || '').replace(/\D/g, '');
                                                        if (digits.length === 9) scheduleSsnCheck(formData.ssn, setFieldErrors);
                                                    }}
                                                    className={`add-patient-form-control ${fieldErrors.ssn ? 'is-invalid' : ''}`}
                                                    placeholder="000-00-0000"
                                                />
                                                {fieldErrors.ssn && <div className="add-patient-error-message">{fieldErrors.ssn}</div>}
                                            </div>
                                        </div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="homePhone">Home Phone</label>
                                                <PhoneNumberInput id="homePhone" name="homePhone" value={formData.homePhone} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.homePhone ? 'is-invalid' : ''}`} />
                                                {fieldErrors.homePhone && <div className="add-patient-error-message">{fieldErrors.homePhone}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="cellPhone">Cell Phone <span className="add-patient-required">*</span></label>
                                                <PhoneNumberInput id="cellPhone" name="cellPhone" value={formData.cellPhone} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.cellPhone ? 'is-invalid' : ''}`} />
                                                {fieldErrors.cellPhone && <div className="add-patient-error-message">{fieldErrors.cellPhone}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="fax">Fax</label>
                                                <PhoneNumberInput id="fax" name="fax" value={formData.fax} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.fax ? 'is-invalid' : ''}`} />
                                                {fieldErrors.fax && <div className="add-patient-error-message">{fieldErrors.fax}</div>}
                                            </div>
                                        </div>

                                        <div className="pif-demo-section-label" style={{ marginTop: '8px' }}>Emergency Contact</div>
                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="emergencyContactFirstName">First Name</label>
                                                <input id="emergencyContactFirstName" type="text" name="emergencyContactFirstName" value={formData.emergencyContactFirstName || ''} onChange={handleInputChange} placeholder="e.g., Jane" maxLength={80} className="add-patient-form-control" />
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="emergencyContactLastName">Last Name</label>
                                                <input id="emergencyContactLastName" type="text" name="emergencyContactLastName" value={formData.emergencyContactLastName || ''} onChange={handleInputChange} placeholder="e.g., Doe" maxLength={80} className="add-patient-form-control" />
                                            </div>
                                        </div>
                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="emergencyContactRelation">Relationship to Patient</label>
                                                <select id="emergencyContactRelation" name="emergencyContactRelation" value={formData.emergencyContactRelation || ''} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="">Select Relationship</option>
                                                    {EMERGENCY_CONTACT_RELATIONS.map((relation) => (
                                                        <option key={relation} value={relation}>{relation}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="emergencyContactPhone">Phone</label>
                                                <PhoneNumberInput id="emergencyContactPhone" name="emergencyContactPhone" value={formData.emergencyContactPhone || ''} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.emergencyContactPhone ? 'is-invalid' : ''}`} />
                                                {fieldErrors.emergencyContactPhone && <div className="add-patient-error-message">{fieldErrors.emergencyContactPhone}</div>}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="emergencyContactAddress">Address</label>
                                                <input id="emergencyContactAddress" type="text" name="emergencyContactAddress" value={formData.emergencyContactAddress || ''} onChange={handleInputChange} placeholder="e.g., 123 Main St, New York, NY 10001" maxLength={250} className="add-patient-form-control" />
                                            </div>
                                        </div>

                                        <div className="pif-demo-section-label" style={{ marginTop: '8px' }}>Account Credentials</div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="username">Username <span className="add-patient-required">*</span></label>
                                                <input id="username" type="text" name="username" value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setFieldErrors)} onBlur={() => handleUsernameBlur(formData.username, setFieldErrors)} placeholder="e.g., john.doe" required maxLength={USERNAME_MAX_LENGTH} className={`add-patient-form-control ${fieldErrors.username ? 'is-invalid' : ''}`} />
                                                {fieldErrors.username && <div className="add-patient-error-message">{fieldErrors.username}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="email">Email <span className="add-patient-required">*</span></label>
                                                <input id="email" type="email" name="email" value={formData.email} onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setFieldErrors)} placeholder="e.g., patient@example.com" required className={`add-patient-form-control ${fieldErrors.email ? 'is-invalid' : ''}`} />
                                                {fieldErrors.email && <div className="add-patient-error-message">{fieldErrors.email}</div>}
                                            </div>
                                        </div>

                                        <div className="pif-demo-section-label" style={{ marginTop: '8px' }}>Address & Location</div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="addressLine1">Address Line 1 <span className="add-patient-required">*</span></label>
                                                <input id="addressLine1" type="text" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" className={`add-patient-form-control ${fieldErrors.addressLine1 ? 'is-invalid' : ''}`} />
                                                {fieldErrors.addressLine1 && <div className="add-patient-error-message">{fieldErrors.addressLine1}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="addressLine2">Address Line 2</label>
                                                <input id="addressLine2" type="text" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" className="add-patient-form-control" />
                                            </div>
                                        </div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="city">City <span className="add-patient-required">*</span></label>
                                                <input id="city" type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" className={`add-patient-form-control ${fieldErrors.city ? 'is-invalid' : ''}`} />
                                                {fieldErrors.city && <div className="add-patient-error-message">{fieldErrors.city}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="state">State <span className="add-patient-required">*</span></label>
                                                <select id="state" name="state" value={formData.state} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.state ? 'is-invalid' : ''}`}>
                                                    <option value="">- Select -</option>
                                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                {fieldErrors.state && <div className="add-patient-error-message">{fieldErrors.state}</div>}
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="zipCode">Zip Code <span className="add-patient-required">*</span></label>
                                                <input id="zipCode" type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" className={`add-patient-form-control ${fieldErrors.zipCode ? 'is-invalid' : ''}`} />
                                                {fieldErrors.zipCode && <div className="add-patient-error-message">{fieldErrors.zipCode}</div>}
                                            </div>
                                        </div>

                                        <div className="add-patient-form-group mb-3" style={{ maxWidth: '360px' }}>
                                            <label htmlFor="timeZone">Time Zone <span className="add-patient-required">*</span></label>
                                            <select id="timeZone" name="timeZone" value={formData.timeZone} onChange={handleInputChange} className={`add-patient-form-control ${fieldErrors.timeZone ? 'is-invalid' : ''}`}>
                                                <option value="">- Select -</option>
                                                {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                                            </select>
                                            {fieldErrors.timeZone && <div className="add-patient-error-message">{fieldErrors.timeZone}</div>}
                                        </div>

                                        <div className="pif-demo-section-label" style={{ marginTop: '8px' }}>Care Team & Settings</div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="status">Status</label>
                                                <select id="status" name="status" value={formData.status} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="Active">Active</option>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Locked">Locked</option>
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="twoWayAuth">Two Way Authentication</label>
                                                <select id="twoWayAuth" name="twoWayAuth" value={formData.twoWayAuth} onChange={handleInputChange} className="add-patient-form-control">
                                                    <option value="Enabled">Enabled</option>
                                                    <option value="Disabled">Disabled</option>
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group flex-grow-1">
                                                <label htmlFor="rpmStartDate">RPM Start Date <span className="add-patient-required">*</span></label>
                                                <AppDateInput id="rpmStartDate" name="rpmStartDate" value={formData.rpmStartDate} onChange={(v) => handleInputChange({ target: { name: 'rpmStartDate', value: v } })} className={`add-patient-form-control ${fieldErrors.rpmStartDate ? 'is-invalid' : ''}`} />
                                                {fieldErrors.rpmStartDate && <div className="add-patient-error-message">{fieldErrors.rpmStartDate}</div>}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <PatientAscStyleMultiPicker
                                                label="Practice caregivers"
                                                required
                                                selectedIds={formData.practiceCaregiverIds}
                                                options={practiceCaregiverPickerOptions}
                                                onAddId={addPracticeCaregiverId}
                                                onRemoveId={removePracticeCaregiverId}
                                                error={fieldErrors.practiceCaregiver}
                                                placeholder="Select practice caregivers"
                                                emptyMessage="No practice caregivers available"
                                            />
                                        </div>

                                        <div className="d-flex gap-3 mb-3">
                                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                                <PatientAscStyleSinglePicker
                                                    label="Provider"
                                                    name="provider"
                                                    required
                                                    value={formData.provider}
                                                    options={providerPickerOptions}
                                                    error={fieldErrors.provider}
                                                    placeholder="Select provider"
                                                    emptyMessage="No providers available"
                                                    onPick={pickProviderField}
                                                />
                                            </div>
                                            {requiresSystemCaregiver && (
                                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                                <PatientAscStyleMultiPicker
                                                    label="System caregivers"
                                                    required
                                                    selectedIds={formData.systemCaregiverIds}
                                                    options={systemCaregiverPickerOptions}
                                                    onAddId={addSystemCaregiverId}
                                                    onRemoveId={removeSystemCaregiverId}
                                                    error={fieldErrors.systemCaregiver}
                                                    placeholder="Select system caregivers"
                                                    emptyMessage="No system caregivers available"
                                                />
                                            </div>
                                            )}
                                        </div>

                                        <div className="add-patient-section-wrapper p-edit-section-in-form">
                                            <div className="add-patient-section-header"><h3>Monitoring type</h3></div>
                                            <div className="add-patient-section-content add-patient-monitoring-content">
                                                {(practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BP) || monitoringTypes.bloodPressure) && (
                                                    <div className="add-patient-monitoring-row">
                                                        <div className="add-patient-monitoring-check-col">
                                                            <input
                                                                type="checkbox"
                                                                id="m-bp"
                                                                checked={monitoringTypes.bloodPressure}
                                                                disabled={monitoringTypes.bloodPressure && !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BP)}
                                                                onChange={() => {
                                                                    if (!practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BP)) return;
                                                                    setMonitoringTypes(prev => ({ ...prev, bloodPressure: !prev.bloodPressure }));
                                                                }}
                                                            />
                                                            <label htmlFor="m-bp">
                                                                Blood Pressure
                                                                {monitoringTypes.bloodPressure && !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BP) ? ' (view only)' : ''}
                                                            </label>
                                                        </div>
                                                        <div className="add-patient-monitoring-select-col">
                                                            {(() => {
                                                                const options = getIcdOptionsForMonitoringLabel('Blood Pressure', icdCodesByMeasureType);
                                                                const currentIcd = monitoringValues.bloodPressure || '';
                                                                const viewOnly = !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BP);
                                                                return (
                                                                    <IcdCodeSearchField
                                                                        value={currentIcd}
                                                                        localOptions={options}
                                                                        disabled={!monitoringTypes.bloodPressure || viewOnly}
                                                                        onChange={(code) => setMonitoringValues((prev) => ({ ...prev, bloodPressure: code }))}
                                                                        ariaLabel="ICD code for Blood Pressure"
                                                                        inputClassName="add-patient-form-control"
                                                                    />
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                                {(practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BG) || monitoringTypes.bloodGlucose) && (
                                                    <div className="add-patient-monitoring-row">
                                                        <div className="add-patient-monitoring-check-col">
                                                            <input
                                                                type="checkbox"
                                                                id="m-bg"
                                                                checked={monitoringTypes.bloodGlucose}
                                                                disabled={monitoringTypes.bloodGlucose && !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BG)}
                                                                onChange={() => {
                                                                    if (!practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BG)) return;
                                                                    setMonitoringTypes(prev => ({ ...prev, bloodGlucose: !prev.bloodGlucose }));
                                                                }}
                                                            />
                                                            <label htmlFor="m-bg">
                                                                Blood Glucose
                                                                {monitoringTypes.bloodGlucose && !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BG) ? ' (view only)' : ''}
                                                            </label>
                                                        </div>
                                                        <div className="add-patient-monitoring-select-col">
                                                            {(() => {
                                                                const options = getIcdOptionsForMonitoringLabel('Blood Glucose', icdCodesByMeasureType);
                                                                const currentIcd = monitoringValues.bloodGlucose || '';
                                                                const viewOnly = !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.BG);
                                                                return (
                                                                    <IcdCodeSearchField
                                                                        value={currentIcd}
                                                                        localOptions={options}
                                                                        disabled={!monitoringTypes.bloodGlucose || viewOnly}
                                                                        onChange={(code) => setMonitoringValues((prev) => ({ ...prev, bloodGlucose: code }))}
                                                                        ariaLabel="ICD code for Blood Glucose"
                                                                        inputClassName="add-patient-form-control"
                                                                    />
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                                {(practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.WT) || monitoringTypes.weight) && (
                                                    <div className="add-patient-monitoring-row">
                                                        <div className="add-patient-monitoring-check-col">
                                                            <input
                                                                type="checkbox"
                                                                id="m-wt"
                                                                checked={monitoringTypes.weight}
                                                                disabled={monitoringTypes.weight && !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.WT)}
                                                                onChange={() => {
                                                                    if (!practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.WT)) return;
                                                                    setMonitoringTypes(prev => ({ ...prev, weight: !prev.weight }));
                                                                }}
                                                            />
                                                            <label htmlFor="m-wt">
                                                                Weight
                                                                {monitoringTypes.weight && !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.WT) ? ' (view only)' : ''}
                                                            </label>
                                                        </div>
                                                        <div className="add-patient-monitoring-select-col">
                                                            {(() => {
                                                                const options = getIcdOptionsForMonitoringLabel('Weight', icdCodesByMeasureType);
                                                                const currentIcd = monitoringValues.weight || '';
                                                                const viewOnly = !practiceMonitoringTypes.includes(MEASURED_TYPE_IDS.WT);
                                                                return (
                                                                    <IcdCodeSearchField
                                                                        value={currentIcd}
                                                                        localOptions={options}
                                                                        disabled={!monitoringTypes.weight || viewOnly}
                                                                        onChange={(code) => setMonitoringValues((prev) => ({ ...prev, weight: code }))}
                                                                        ariaLabel="ICD code for Weight"
                                                                        inputClassName="add-patient-form-control"
                                                                    />
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                                {practiceMonitoringTypes.length === 0 && !monitoringTypes.bloodPressure && !monitoringTypes.bloodGlucose && !monitoringTypes.weight && (
                                                    <div className="alert alert-warning" style={{ width: '100%' }}>No monitoring types configured for this practice. Please configure monitoring types in practice settings.</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="add-patient-section-wrapper p-edit-section-in-form">
                                            <div className="add-patient-section-header"><h3>Insurance</h3></div>
                                            <div className="add-patient-section-content">
                                                <div className="add-patient-insurance-grid">
                                                    <div className="add-patient-insurance-label-col"></div>
                                                    <div className="add-patient-insurance-header-col"><label>Insurance Name</label></div>
                                                    <div className="add-patient-insurance-header-col"><label>Subscriber Id</label></div>
                                                    <div className="add-patient-insurance-header-col"><label>Insured's Name</label></div>
                                                    <div className="add-patient-insurance-header-col"><label>Group No.</label></div>

                                                    <div className="add-patient-insurance-label-col label-text">Primary</div>
                                                    <div className="add-patient-insurance-input-col">
                                                        <input
                                                            type="text"
                                                            className={`add-patient-form-control ${fieldErrors.primaryInsuranceName ? 'is-invalid' : ''}`}
                                                            value={insuranceData.primary.insuranceName}
                                                            onChange={(e) => handleInsuranceChange('primary', 'insuranceName', e.target.value)}
                                                            onBlur={() => handleInsuranceBlur('primary', 'insuranceName')}
                                                            autoComplete="off"
                                                        />
                                                        {fieldErrors.primaryInsuranceName && <div className="add-patient-error-message">{fieldErrors.primaryInsuranceName}</div>}
                                                    </div>
                                                    <div className="add-patient-insurance-input-col">
                                                        <input
                                                            type="text"
                                                            className={`add-patient-form-control ${fieldErrors.primarySubscriberId ? 'is-invalid' : ''}`}
                                                            value={insuranceData.primary.subscriberId}
                                                            onChange={(e) => handleInsuranceChange('primary', 'subscriberId', e.target.value)}
                                                            onBlur={() => handleInsuranceBlur('primary', 'subscriberId')}
                                                            maxLength={20}
                                                            onPaste={(e) => {
                                                                e.preventDefault();
                                                                const pasted = e.clipboardData?.getData('text') || '';
                                                                handleInsuranceChange('primary', 'subscriberId', pasted);
                                                            }}
                                                            autoComplete="off"
                                                        />
                                                        {fieldErrors.primarySubscriberId && <div className="add-patient-error-message">{fieldErrors.primarySubscriberId}</div>}
                                                    </div>
                                                    <div className="add-patient-insurance-input-col">
                                                        <input
                                                            type="text"
                                                            className={`add-patient-form-control ${fieldErrors.primaryInsuredName ? 'is-invalid' : ''}`}
                                                            value={insuranceData.primary.insuredName}
                                                            onChange={(e) => handleInsuranceChange('primary', 'insuredName', e.target.value)}
                                                            onBlur={() => handleInsuranceBlur('primary', 'insuredName')}
                                                            autoComplete="off"
                                                        />
                                                        {fieldErrors.primaryInsuredName && <div className="add-patient-error-message">{fieldErrors.primaryInsuredName}</div>}
                                                    </div>
                                                    <div className="add-patient-insurance-input-col">
                                                        <input
                                                            type="text"
                                                            className={`add-patient-form-control ${fieldErrors.primaryGroupNo ? 'is-invalid' : ''}`}
                                                            value={insuranceData.primary.groupNo}
                                                            onChange={(e) => handleInsuranceChange('primary', 'groupNo', e.target.value)}
                                                            onBlur={() => handleInsuranceBlur('primary', 'groupNo')}
                                                            maxLength={15}
                                                            autoComplete="off"
                                                        />
                                                        {fieldErrors.primaryGroupNo && <div className="add-patient-error-message">{fieldErrors.primaryGroupNo}</div>}
                                                    </div>

                                                    <div className="add-patient-insurance-label-col label-text">Secondary</div>
                                                    <div className="add-patient-insurance-input-col">
                                                        <input
                                                            type="text"
                                                            className={`add-patient-form-control ${fieldErrors.secondaryInsuranceName ? 'is-invalid' : ''}`}
                                                            value={insuranceData.secondary.insuranceName}
                                                            onChange={(e) => handleInsuranceChange('secondary', 'insuranceName', e.target.value)}
                                                            onBlur={() => handleInsuranceBlur('secondary', 'insuranceName')}
                                                            autoComplete="off"
                                                        />
                                                        {fieldErrors.secondaryInsuranceName && <div className="add-patient-error-message">{fieldErrors.secondaryInsuranceName}</div>}
                                                    </div>
                                                    <div className="add-patient-insurance-input-col">
                                                        <input
                                                            type="text"
                                                            className={`add-patient-form-control ${fieldErrors.secondarySubscriberId ? 'is-invalid' : ''}`}
                                                            value={insuranceData.secondary.subscriberId}
                                                            onChange={(e) => handleInsuranceChange('secondary', 'subscriberId', e.target.value)}
                                                            onBlur={() => handleInsuranceBlur('secondary', 'subscriberId')}
                                                            maxLength={20}
                                                            onPaste={(e) => {
                                                                e.preventDefault();
                                                                const pasted = e.clipboardData?.getData('text') || '';
                                                                handleInsuranceChange('secondary', 'subscriberId', pasted);
                                                            }}
                                                            autoComplete="off"
                                                        />
                                                        {fieldErrors.secondarySubscriberId && <div className="add-patient-error-message">{fieldErrors.secondarySubscriberId}</div>}
                                                    </div>
                                                    <div className="add-patient-insurance-input-col">
                                                        <input
                                                            type="text"
                                                            className={`add-patient-form-control ${fieldErrors.secondaryInsuredName ? 'is-invalid' : ''}`}
                                                            value={insuranceData.secondary.insuredName}
                                                            onChange={(e) => handleInsuranceChange('secondary', 'insuredName', e.target.value)}
                                                            onBlur={() => handleInsuranceBlur('secondary', 'insuredName')}
                                                            autoComplete="off"
                                                        />
                                                        {fieldErrors.secondaryInsuredName && <div className="add-patient-error-message">{fieldErrors.secondaryInsuredName}</div>}
                                                    </div>
                                                    <div className="add-patient-insurance-input-col">
                                                        <input
                                                            type="text"
                                                            className={`add-patient-form-control ${fieldErrors.secondaryGroupNo ? 'is-invalid' : ''}`}
                                                            value={insuranceData.secondary.groupNo}
                                                            onChange={(e) => handleInsuranceChange('secondary', 'groupNo', e.target.value)}
                                                            onBlur={() => handleInsuranceBlur('secondary', 'groupNo')}
                                                            maxLength={15}
                                                            autoComplete="off"
                                                        />
                                                        {fieldErrors.secondaryGroupNo && <div className="add-patient-error-message">{fieldErrors.secondaryGroupNo}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="add-patient-section-wrapper add-patient-section-wrapper--in-block mt-3">
                                            <div className="add-patient-section-content p-3">
                                                <PatientDocumentsSection
                                                    ref={documentsSectionRef}
                                                    practiceId={practiceId}
                                                    patientId={patientId}
                                                    isEditMode
                                                    showSavedTable
                                                    showUploadRows
                                                    showUpdateButton
                                                    title="Uploaded documents"
                                                    onPendingDocumentsChange={setPendingDocumentCount}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="add-patient-form-actions p-edit-form-actions-row">
                                        <button
                                            type="button"
                                            className="p-edit-btn-event-log"
                                            onClick={() => openPatientsLog(LOG_MODULE_PATIENT)}
                                        >
                                            <FontAwesomeIcon icon={faHistory} /> Event Logs
                                        </button>
                                        <div className="p-edit-form-actions-right">
                                            <button
                                                type="button"
                                                className="add-patient-btn add-patient-btn-cancel"
                                                onClick={() => navigate(-1)}
                                                disabled={isSubmitting || !isChanged}
                                            >
                                                Cancel
                                            </button>
                                            <button type="submit" className="add-patient-btn add-patient-btn-add" disabled={isSubmitting || !isChanged}>
                                                {isSubmitting ? (<><FontAwesomeIcon icon={faSpinner} spin /> Updating...</>) : (<><FontAwesomeIcon icon={faSave} /> Update</>)}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {activeTab === 'schedule' && (
                        <div className="p-edit-tab-pane p-edit-tab-with-log-footer">
                            <div className="p-edit-schedule-scroll">
                                <ScheduleTargetSettings
                                    practiceId={practiceId}
                                    patientId={patientId}
                                    showSectionEventLogButtons
                                    onOpenEventLog={(section) =>
                                        openPatientsLog(LOG_MODULE_SCHEDULE, section || null)
                                    }
                                    enabledMonitoring={{
                                        bp: Boolean(monitoringTypes.bloodPressure),
                                        bg: Boolean(monitoringTypes.bloodGlucose),
                                        weight: Boolean(monitoringTypes.weight)
                                    }}
                                    practiceEnabledMonitoring={getPracticeMonitoringSectionFlags(practiceMonitoringTypes)}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'meter-mapping' && (
                        <div className="p-edit-tab-pane">
                            <div className="add-patient-form-card p-edit-addpatient-card p-edit-inner-pane" style={{ paddingTop: '10px' }}>

                                <div className="add-patient-section-wrapper mt-4">
                                    <div className="add-patient-section-header">
                                        <h3>Meter Mapping</h3>
                                    </div>
                                    <div className="add-patient-section-content">
                                        <div className="add-patient-meter-mapping-row">
                                            <div className="add-patient-form-group">
                                                <label>Model number</label>
                                                <select value={meterForm.modelNumber} onChange={(e) => setMeterForm({ ...meterForm, modelNumber: e.target.value })} className="add-patient-form-control">
                                                    <option value="">- Select -</option>
                                                    {products.map(p => <option key={p.id || p.model_number} value={p.model_number}>{formatMeterProductOptionLabel(p)}</option>)}
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group">
                                                <label>Extension</label>
                                                <select value={METER_EXTENSION_OPTIONS.some(o => String(o.value) === String(meterForm.extension)) ? String(meterForm.extension) : ''} onChange={(e) => setMeterForm({ ...meterForm, extension: e.target.value })} className="add-patient-form-control">
                                                    <option value="">- Select -</option>
                                                    {METER_EXTENSION_OPTIONS.map(ext => (
                                                        <option key={ext.value} value={ext.value}>{ext.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="add-patient-form-group add-patient-meter-serial">
                                                <label>Serial number</label>
                                                <input type="text" value={meterForm.serialNumber} maxLength={16} onChange={(e) => setMeterForm({ ...meterForm, serialNumber: normalizeDeviceSerialInput(e.target.value) })} className="add-patient-form-control" placeholder="16-character alphanumeric" />
                                            </div>
                                            <div className="add-patient-form-group">
                                                <label>Gateway serial number</label>
                                                <input type="text" value={meterForm.gatewaySerialNumber} onChange={(e) => setMeterForm({ ...meterForm, gatewaySerialNumber: e.target.value })} className="add-patient-form-control" />
                                            </div>
                                            <div className="add-patient-action-group">
                                                <label>&nbsp;</label>
                                                <button type="button" className="add-patient-btn add-patient-btn-add" onClick={handleAddMeterToTable}>
                                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                    Add meter
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="add-patient-section-wrapper mt-4">
                                    <div className="add-patient-section-header">
                                        <h3>Mapped Devices</h3>
                                    </div>
                                    <div className="manage-meter-table-wrap">
                                        <table className="table table-hover mb-0" style={{ fontSize: '14px', width: '100%', tableLayout: 'fixed' }}>
                                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                <tr>
                                                    <th style={{ width: '50px', paddingLeft: '15px', textAlign: 'left' }}>#</th>
                                                    <th style={{ width: '20%', textAlign: 'left' }}>Product Name</th>
                                                    <th style={{ width: '15%', textAlign: 'left' }}>Extension</th>
                                                    <th style={{ width: '25%', textAlign: 'left' }}>Serial No</th>
                                                    <th style={{ width: '25%', textAlign: 'left' }}>Gateway No</th>
                                                    <th style={{ width: '120px', paddingRight: '15px', textAlign: 'center' }}>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mappedDevices.length > 0 ? (
                                                    mappedDevices.map((m, idx) => (
                                                        <tr key={m.id != null ? `meter-${m.id}` : m._localKey || `meter-${idx}`}>
                                                            <td style={{ paddingLeft: '15px', textAlign: 'left', verticalAlign: 'middle' }}>{idx + 1}</td>
                                                            <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{resolveMeterProductDisplayLabel(m.model_number, products)}</td>
                                                            <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{formatMeterExtensionLabel(m.extension)}</td>
                                                            <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{m.serial_number || 'N/A'}</td>
                                                            <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>{m.gateway_serial_number || 'N/A'}</td>
                                                            <td style={{ paddingRight: '15px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                                <div className="d-flex justify-content-center">
                                                                    <ActionButtons
                                                                        hideView
                                                                        onEdit={() => handleEditMeterRow(m)}
                                                                        onDelete={() => handleDeleteMeterRow(m)}
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="6" className="text-center py-4 text-muted">No devices mapped</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="p-edit-section-event-log-bar">
                                    <button type="button" className="p-edit-btn-event-log" onClick={() => openPatientsLog(LOG_MODULE_METER)}>
                                        <FontAwesomeIcon icon={faHistory} /> Event Logs
                                    </button>
                                </div>
                                <div className="mt-3">
                                    <h6 style={{ color: '#263651', fontWeight: 600, marginBottom: '10px' }}>Meter Mapping Event Logs</h6>
                                    <PatientsLogContent
                                        show={true}
                                        moduleId={LOG_MODULE_METER}
                                        patientId={patientId != null && String(patientId).trim() !== '' ? parseInt(String(patientId), 10) || patientId : null}
                                        practiceId={practiceId != null && String(practiceId).trim() !== '' ? parseInt(String(practiceId), 10) || practiceId : null}
                                        patientUsername={formData.username || patientData?.username || ''}
                                patientFirstName={formData.first_name || patientData?.first_name || ''}
                                patientLastName={formData.last_name || patientData?.last_name || ''}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'uploaded-documents' && (
                        <div className="p-edit-tab-pane">
                            <div className="add-patient-form-card p-edit-addpatient-card p-edit-inner-pane" style={{ paddingTop: '16px' }}>
                                <div style={{ padding: '0 12px' }}>
                                    <PatientDocumentsSection
                                        practiceId={practiceId}
                                        patientId={patientId}
                                        isEditMode
                                        showSavedTable
                                        showUploadRows
                                        showUpdateButton
                                        title="Uploaded documents"
                                    />
                                </div>
                                <div className="p-edit-section-event-log-bar">
                                    <button type="button" className="p-edit-btn-event-log" onClick={() => openPatientsLog(LOG_MODULE_DOCUMENT)}>
                                        <FontAwesomeIcon icon={faHistory} /> Event Logs
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'programs' && (
                        <div className="p-edit-tab-pane p-edit-patient-info-tab">
                            <div className="add-patient-form-card p-edit-inner-pane" style={{ paddingTop: '16px' }}>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h5 className="add-patient-block-title mb-0" style={{ borderBottom: 'none', paddingBottom: 0 }}>Patient Programs</h5>
                                    <button type="button" className="btn btn-primary" onClick={handleSavePrograms} style={{ backgroundColor: '#1e3a5f', borderColor: '#1e3a5f', padding: '8px 16px', borderRadius: '6px', fontWeight: 500 }}>
                                        <FontAwesomeIcon icon={faSave} className="me-2" /> Update Programs
                                    </button>
                                </div>
                                <p className="text-muted small mb-3">
                                    PCM is for one high-risk chronic condition (CPT 99424–99427). CCM is for two or more. A patient cannot be enrolled in both.
                                </p>
                                <div className="table-responsive">
                                    <table className="table table-hover" style={{ verticalAlign: 'middle' }}>
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th style={{ width: '60px', paddingLeft: '15px' }}>Enrolled</th>
                                                <th>Program Name</th>
                                                <th>Description</th>
                                                <th style={{ paddingRight: '15px' }}>Enrollment Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {patientPrograms.length > 0 ? patientPrograms.map(prog => (
                                                <tr key={prog.id} style={{ cursor: 'pointer' }} onClick={() => handleToggleProgram(prog)}>
                                                    <td style={{ paddingLeft: '15px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!prog.enrolled}
                                                            onChange={() => { }} // Controlled via row click
                                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span
                                                                style={{
                                                                    backgroundColor: prog.badge_color || '#64748b',
                                                                    color: '#fff',
                                                                    padding: '4px 8px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {prog.short_name}
                                                            </span>
                                                            <span style={{ fontWeight: 500 }}>{prog.full_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="text-muted">{prog.description || '-'}</td>
                                                    <td style={{ paddingRight: '15px' }}>{prog.enrolled && prog.enrollment_date ? formatDate(prog.enrollment_date) : '-'}</td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="4" className="text-center py-4 text-muted">No care programs available for this practice</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Disenrollment Confirmation Modal */}
                {programConfirmModal.isOpen && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 105000 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
                                <div className="modal-header" style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#f8f9fa', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="modal-title" style={{ fontWeight: 600, color: '#263651' }}>Confirm Disenrollment</h5>
                                    <button type="button" className="btn-close" onClick={() => setProgramConfirmModal({ isOpen: false, program: null })}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <p style={{ fontSize: '15px', marginBottom: '12px' }}>
                                        Are you sure you want to remove the patient from the <strong style={{ color: '#d9534f' }}>{programConfirmModal.program?.full_name} ({programConfirmModal.program?.short_name})</strong> program?
                                    </p>
                                    <p className="text-muted small mb-0" style={{ backgroundColor: '#fff4f4', padding: '10px', borderRadius: '6px', borderLeft: '3px solid #d9534f' }}>
                                        This action will mark the program as inactive for this patient.
                                    </p>
                                </div>
                                <div className="modal-footer" style={{ borderTop: '1px solid #f0f0f0' }}>
                                    <button type="button" className="btn btn-light" onClick={() => setProgramConfirmModal({ isOpen: false, program: null })} style={{ fontWeight: 500 }}>Cancel</button>
                                    <button type="button" className="btn btn-danger" onClick={handleConfirmDisenroll} style={{ fontWeight: 500, backgroundColor: '#dc3545', borderColor: '#dc3545' }}>Yes, Remove Patient</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* RPM Enrollment Modal */}
                {rpmEnrollModal.isOpen && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 105000 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
                                <div className="modal-header" style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#f8f9fa', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="modal-title" style={{ fontWeight: 600, color: '#263651' }}>Enroll in RPM</h5>
                                    <button type="button" className="btn-close" onClick={() => setRpmEnrollModal({ isOpen: false, program: null, rpmStartDate: '' })}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <p style={{ fontSize: '15px', marginBottom: '16px' }}>
                                        Enrolling this patient in RPM will register them with ForaCare for device monitoring.
                                    </p>
                                    <label htmlFor="rpmEnrollStartDate" className="form-label" style={{ fontWeight: 500 }}>
                                        RPM Start Date <span className="add-patient-required">*</span>
                                    </label>
                                    <AppDateInput
                                        id="rpmEnrollStartDate"
                                        className="form-control"
                                        value={rpmEnrollModal.rpmStartDate}
                                        onChange={(v) => setRpmEnrollModal(prev => ({ ...prev, rpmStartDate: v }))}
                                    />
                                </div>
                                <div className="modal-footer" style={{ borderTop: '1px solid #f0f0f0' }}>
                                    <button type="button" className="btn btn-light" onClick={() => setRpmEnrollModal({ isOpen: false, program: null, rpmStartDate: '' })} style={{ fontWeight: 500 }}>Cancel</button>
                                    <button type="button" className="btn btn-primary" onClick={handleConfirmRpmEnroll} style={{ fontWeight: 500, backgroundColor: '#1e3a5f', borderColor: '#1e3a5f' }}>Enroll in RPM</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Patient Status Device Removal Confirmation Modal */}
                <PatientStatusDeviceRemovalModal
                    show={statusDeviceRemovalModal.show}
                    patientName={[patientData?.first_name || formData.firstName, patientData?.last_name || formData.lastName].filter(Boolean).join(' ') || 'this patient'}
                    targetStatus={statusDeviceRemovalModal.targetStatus}
                    devices={mappedDevices}
                    onConfirm={handleConfirmStatusDeviceRemoval}
                    onCancel={handleCancelStatusDeviceRemoval}
                />
            </div>
        </div>
    );
}
